import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import EventLayout from "@/components/event/EventLayout";
import { Button } from "@/components/ui/button";
import { eventApi } from "@/lib/eventHub";
import { Loader2, CheckCircle2 } from "lucide-react";

const INTERESTS = [
  "Attend", "Apply as an Honoree", "Sponsor", "Volunteer",
  "Partner", "Vendor", "Speaker", "Receive Updates",
];

export default function EarlyAccess() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    organization: "", city: "", state: "",
    interest_types: [], consent_sms: false, consent_email: true,
    referral_source: "", campaign_source: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => { try { const c = await eventApi.config(); setConfig(c.data); } catch { /* noop */ } })();
    // Capture campaign_source and referral_source from URL
    try {
      const q = new URLSearchParams(window.location.search);
      setForm((f) => ({
        ...f,
        campaign_source: q.get("utm_campaign") || q.get("campaign") || "",
        referral_source: q.get("utm_source") || q.get("ref") || document.referrer || "",
      }));
    } catch { /* noop */ }
  }, []);

  const toggle = (i) => {
    setForm((f) => ({
      ...f,
      interest_types: f.interest_types.includes(i) ? f.interest_types.filter((x) => x !== i) : [...f.interest_types, i],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email) {
      toast.error("Please fill out your name and email.");
      return;
    }
    setLoading(true);
    try {
      await eventApi.earlyAccess(form);
      setDone(true);
      eventApi.track("early_access_complete");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <EventLayout showProgramBanner={false}>
        <div className="mx-auto max-w-lg px-5 py-16 text-center">
          <CheckCircle2 className="mx-auto text-fuchsia-600" size={72} />
          <h1 className="font-[Cormorant_Garamond] text-4xl leading-tight text-emerald-950 mt-4 mb-2">You’re on the list!</h1>
          <p className="font-[Outfit] text-sm text-emerald-950/70 max-w-xs mx-auto">
            We’ll email you when 2027 details go live — you’ll be first in line.
          </p>
          <Button asChild className="mt-6 rounded-full bg-emerald-950 text-white px-6 h-12 font-[Outfit] font-semibold">
            <Link to="/event">Back to Event Hub</Link>
          </Button>
        </div>
      </EventLayout>
    );
  }

  return (
    <EventLayout showProgramBanner={false}>
      <div className="mx-auto max-w-lg px-5 py-6">
        <h1 className="font-[Cormorant_Garamond] text-3xl sm:text-4xl leading-tight text-emerald-950">
          2027 Early Access
        </h1>
        <p className="font-[Outfit] text-sm text-emerald-950/60 mt-1 mb-6">
          Be first for tickets, honoree applications, sponsorships, and more.
        </p>

        <form onSubmit={submit} data-testid="early-access-form" className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <input
              data-testid="ea-first-name"
              required placeholder="First name *" autoComplete="given-name"
              value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              className="h-12 rounded-xl border-2 border-emerald-950/10 bg-white px-3 font-[Outfit] text-sm focus:border-fuchsia-500 outline-none"
            />
            <input
              data-testid="ea-last-name"
              required placeholder="Last name *" autoComplete="family-name"
              value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              className="h-12 rounded-xl border-2 border-emerald-950/10 bg-white px-3 font-[Outfit] text-sm focus:border-fuchsia-500 outline-none"
            />
          </div>
          <input
            data-testid="ea-email"
            required type="email" placeholder="Email *" autoComplete="email"
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full h-12 rounded-xl border-2 border-emerald-950/10 bg-white px-3 font-[Outfit] text-sm focus:border-fuchsia-500 outline-none"
          />
          <input
            data-testid="ea-phone"
            type="tel" inputMode="tel" placeholder="Mobile number" autoComplete="tel"
            value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="w-full h-12 rounded-xl border-2 border-emerald-950/10 bg-white px-3 font-[Outfit] text-sm focus:border-fuchsia-500 outline-none"
          />
          <input
            data-testid="ea-organization"
            placeholder="Business or organization"
            value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })}
            className="w-full h-12 rounded-xl border-2 border-emerald-950/10 bg-white px-3 font-[Outfit] text-sm focus:border-fuchsia-500 outline-none"
          />
          <div className="grid grid-cols-2 gap-2">
            <input
              data-testid="ea-city"
              placeholder="City"
              value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
              className="h-12 rounded-xl border-2 border-emerald-950/10 bg-white px-3 font-[Outfit] text-sm focus:border-fuchsia-500 outline-none"
            />
            <input
              data-testid="ea-state"
              placeholder="State" maxLength="2"
              value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })}
              className="h-12 rounded-xl border-2 border-emerald-950/10 bg-white px-3 font-[Outfit] text-sm focus:border-fuchsia-500 outline-none uppercase"
            />
          </div>

          <div>
            <div className="text-[11px] font-[Outfit] font-semibold uppercase tracking-wider text-emerald-950/70 mb-2">
              I’m interested in
            </div>
            <div className="flex flex-wrap gap-1.5">
              {INTERESTS.map((i) => {
                const active = form.interest_types.includes(i);
                return (
                  <button
                    type="button" key={i}
                    data-testid={`ea-interest-${i}`}
                    onClick={() => toggle(i)}
                    className={`text-xs px-3 py-1.5 rounded-full font-[Outfit] font-semibold transition-all ${
                      active ? "bg-fuchsia-600 text-white" : "bg-white border border-emerald-950/15 text-emerald-950"
                    }`}
                  >
                    {i}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5 pt-2 text-xs font-[Outfit] text-emerald-950/70">
            <label className="flex items-start gap-2">
              <input
                data-testid="ea-consent-email"
                type="checkbox"
                checked={form.consent_email}
                onChange={(e) => setForm({ ...form, consent_email: e.target.checked })}
                className="mt-0.5 h-4 w-4 rounded border-emerald-950/20 text-fuchsia-600 focus:ring-fuchsia-500"
              />
              <span>Email me updates about 2027 Business Showers and honoree opportunities.</span>
            </label>
            <label className="flex items-start gap-2">
              <input
                data-testid="ea-consent-sms"
                type="checkbox"
                checked={form.consent_sms}
                onChange={(e) => setForm({ ...form, consent_sms: e.target.checked })}
                className="mt-0.5 h-4 w-4 rounded border-emerald-950/20 text-fuchsia-600 focus:ring-fuchsia-500"
              />
              <span>
                Text me when 2027 details are announced. Message and data rates may apply.
                Message frequency: 1–3 msgs per event cycle. Reply STOP to opt out, HELP for help. See{" "}
                <a href="/privacy" target="_blank" rel="noreferrer" className="underline text-fuchsia-700 font-semibold">Privacy</a>
                {" & "}
                <a href="/terms" target="_blank" rel="noreferrer" className="underline text-fuchsia-700 font-semibold">Terms</a>.
              </span>
            </label>
          </div>

          <Button
            type="submit"
            disabled={loading}
            data-testid="ea-submit"
            className="w-full h-14 rounded-xl bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-[Outfit] font-bold text-base"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : "Join the 2027 List"}
          </Button>
        </form>
      </div>
    </EventLayout>
  );
}
